import {
  AlertTriangle,
  ArrowRight,
  Button,
  Shield,
} from "@ews-admin/global-design-system";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { envConfig, getApplicationUrl } from "../../utils/config-utils";

const REDIRECT_DELAY_SECONDS = 6;

interface UnauthorizedAccessProps {
  userRole?: string;
  requiredRole?: string;
}

export const UnauthorizedAccess: React.FC<UnauthorizedAccessProps> = ({
  userRole,
  requiredRole = "admin",
}) => {
  const { t } = useTranslation();
  const [countdown, setCountdown] = useState(REDIRECT_DELAY_SECONDS);

  const handleRedirectToMainApp = useCallback(() => {
    const mainAppUrl = getApplicationUrl();
    if (mainAppUrl) {
      window.location.href = mainAppUrl;
    } else {
      // Fallback to environment variable or default
      window.location.href = envConfig.appUrl || window.location.origin;
    }
  }, []);

  // MED-1483: the redirect used to fire from inside the setState updater. An
  // updater must be pure — React re-invokes it (twice in StrictMode, and
  // again on any re-render it replays), so the navigation could be triggered
  // before the screen had counted down. Tick the number here, and let a
  // separate effect below react to it reaching zero.
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (countdown === 0) {
      handleRedirectToMainApp();
    }
  }, [countdown, handleRedirectToMainApp]);

  return (
    <div className="flex flex-col justify-center py-12 min-h-screen bg-gray-50 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <div className="flex justify-center items-center w-16 h-16 bg-red-100 rounded-full">
            <Shield className="w-8 h-8 text-red-600" />
          </div>
        </div>
        <h2 className="mt-6 text-3xl font-bold text-center text-gray-900">
          {t("auth.unauthorized.title")}
        </h2>
        <p className="mt-2 text-sm text-center text-gray-600">
          {t("auth.unauthorized.subtitle")}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="px-4 py-8 bg-white shadow sm:rounded-lg sm:px-10">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="flex-shrink-0 w-5 h-5 text-yellow-500 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-gray-900">
                {t("auth.unauthorized.accessDenied")}
              </h3>
              <div className="mt-2 text-sm text-gray-600">
                <p>
                  {t("auth.unauthorized.message", {
                    userRole: userRole || t("common.unknown"),
                    requiredRole: requiredRole,
                  })}
                </p>
                <p className="mt-2">{t("auth.unauthorized.redirectMessage")}</p>
              </div>
            </div>
          </div>

          {/* Countdown display */}
          <div className="p-4 mt-6 bg-blue-50 rounded-lg">
            <div className="flex justify-center items-center space-x-3">
              <div className="flex justify-center items-center w-12 h-12 text-lg font-bold text-white bg-blue-600 rounded-full">
                {countdown}
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-blue-800">
                  {t("auth.unauthorized.countdown", { count: countdown })}
                </p>
                <p className="mt-1 text-xs text-blue-600">
                  {t("auth.unauthorized.autoRedirect")}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <Button onClick={handleRedirectToMainApp}>
              <span>{t("auth.unauthorized.goToMainApp")}</span>
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
